import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

import UploadManager from './UploadManager';

const useUploadFiles = (files, { getSession = () => {}, onEdit = () => {}, onFileUploaded = () => {} }) => {
  const [filesToUpload, setFilesToUpload] = useState([]);
  const uploadManager = useRef(null);
  const [progress, setProgress] = useState({});
  const allFiles = useMemo(() => [...files, ...filesToUpload], [files, filesToUpload]);

  const onFilesUpdate = useCallback(() => {
    const filesToUpload = uploadManager.current?.getFilesToUpload();
    setFilesToUpload(filesToUpload);
    onEdit(uploadManager.current?.getFilesUploaded(), filesToUpload.length === 0);
    
  }, [setFilesToUpload, onEdit]);

  const _onFileUploaded = useCallback((file) => {
    onFileUploaded(file);
    onFilesUpdate();
  }, [onFilesUpdate, onFileUploaded]);

  const onProgress = useCallback((progress) => setProgress(progress), [setProgress]);

  useEffect(() => {
    if (!uploadManager.current) {
      uploadManager.current = new UploadManager({ files: allFiles, getSession });
      onFilesUpdate();
      uploadManager.current.addEventListener('fileUploaded', _onFileUploaded);
      uploadManager.current.addEventListener('progress', onProgress);
    }
  }, [uploadManager, allFiles, onFilesUpdate, onProgress, getSession, _onFileUploaded]);

  const addFiles = (files) => {
    const ids = uploadManager.current?.addFiles(files);
    onFilesUpdate();
    return ids;
  };

  const deleteFile = (idx) => {
    uploadManager.current?.deleteFile(idx);
    onFilesUpdate();
  };

  const deleteAllFiles = () => {
    uploadManager.current?.deleteAllFiles();
    onFilesUpdate();
  };

  const renameFile = (idx, name) => {
    uploadManager.current?.renameFile(idx, name);
    onFilesUpdate();
  };

  return [
    allFiles,
    {
      progress,
      addFiles,
      deleteFile,
      deleteAllFiles,
      renameFile,
    },
  ];
};

export default useUploadFiles;